/**
 * The staging environment, on an app's settings screen.
 *
 * WHAT THIS SCREEN HAS TO GET ACROSS, and it is only two things:
 *
 * 1. A staging environment is a second copy of the app, with its own database
 *    and files, and it is not free. It is never created for anybody
 *    automatically, so this is the one place somebody decides to have one.
 *
 * 2. Turning it ON is the moment pushes stop reaching the live app. Somebody
 *    who does not take that in finds out when a customer asks where the fix
 *    went, so the switch says it in the sentence next to it rather than in a
 *    tooltip, and the way back is always one button away.
 *
 * Every rule behind the switch lives on the server (apps/cloud's
 * services/staging.ts): it refuses until the repository's deploy workflow can
 * actually promote a tag, and it names the settings the live app has that
 * staging does not. This file renders those answers and never re-derives them.
 */

import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '../primitives/Button';
import { Notice } from './Notice';
import { SectionPanel } from './SectionPanel';
import { Skeleton } from './Skeleton';

import {
  createStagingEnvironment,
  disableStaging,
  enableStaging,
  getStagingReadiness,
} from '../../lib/api';
import { captureEvent } from '../../lib/analytics/posthog';
import { ApiError } from '../../lib/http';
import { stagingSlugOf } from '../../lib/environments';
import { useAsync } from '../../lib/hooks/use-async';
import { t } from '../../lib/i18n/t';
import type { Project } from '../../lib/types';

/**
 * The demo seeds one project that already HAS a staging environment, so the
 * two Keys screens, the link between them and the STAGING marker are all
 * reviewable. What it cannot rehearse is provisioning a second app and a second
 * database, so the buttons that would change which environments exist say so
 * rather than pretending.
 */
const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

interface Props {
  project: Project;
  /** Re-read the project after a change, so the rest of the screen agrees. */
  onChanged: () => void;
}

export function StagingSection({ project, onChanged }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const readiness = useAsync(() => getStagingReadiness(project.id), [project.id]);
  const staging = project.environments.staging;
  const staged = project.deployMode === 'staged';
  const branch = project.repo?.branch ?? 'main';

  async function act(
    name: string,
    run: () => Promise<void>,
    extra: Record<string, unknown> = {},
  ): Promise<void> {
    setBusy(true);
    setError(null);
    captureEvent(name, { project_slug: project.slug, ...extra });
    try {
      await run();
      readiness.refetch();
      onChanged();
    } catch (err: unknown) {
      // The server's refusals are the useful text here: which settings are
      // missing, or what the repository's workflow is still lacking. Replacing
      // them with something generic would throw away the only actionable part.
      setError(err instanceof ApiError ? err.message : t('That did not go through. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  const note = staged
    ? t('Pushes to {branch} go to staging. Your live app changes when you push a prod tag.', {
        branch,
      })
    : t(
        'A staging environment is a second copy of your app, with its own database and files, that you can try changes on before they reach the people using it.',
      );

  return (
    <SectionPanel title={t('Staging')} note={note}>
      <div className="set__control">
        {error ? <Notice tone="attention">{error}</Notice> : null}

        {!staging ? (
          <>
            {/* Said before the button, not after it. The cost is the whole
                reason this is a decision rather than a default. */}
            <p className="set__say">
              {t(
                'It runs a second app and a second database, and what it uses comes out of the same allowance as your live app.',
              )}
            </p>
            {USE_MOCK ? (
              <p className="set__say">{t('Adding one is not part of this demo.')}</p>
            ) : (
              <div className="set__actions">
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => act('staging_created', () => createStagingEnvironment(project.id))}
                >
                  {t('Add a staging environment')}
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="set__say">
              {t('Your staging app')}{' '}
              <Link to={`/p/${encodeURIComponent(stagingSlugOf(project) ?? project.slug)}`}>
                {stagingSlugOf(project) ?? project.slug}
              </Link>
            </p>

            {readiness.isLoading ? (
              <Skeleton count={2} label={t('Loading your staging environment')} />
            ) : staged ? (
              <>
                <p className="set__say">
                  {t(
                    'To put a version on your live app, tag the commit you tested: git tag prod-2026-01-01.1 then git push origin prod-2026-01-01.1',
                  )}
                </p>
                {USE_MOCK ? null : (
                  <div className="set__actions">
                    <Button
                      variant="outline"
                      disabled={busy}
                      onClick={() => act('staging_disabled', () => disableStaging(project.id))}
                    >
                      {t('Go back to deploying straight to the live app')}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                {readiness.data && !readiness.data.workflowOk ? (
                  <Notice tone="attention">
                    {readiness.data.workflowProblem ??
                      t('Your repository is not set up to promote a tagged version yet.')}
                  </Notice>
                ) : null}

                {readiness.data && readiness.data.missingSettingNames.length > 0 ? (
                  <Notice tone="attention">
                    {/* The likeliest reason a first staging build fails, and
                        nothing can copy them across: a key's value is never
                        readable once it is set, by anybody including us. */}
                    {t(
                      'Your staging app is missing {count} setting(s) your live app has: {names}. Add them under Keys and settings first, or its first build is likely to fail.',
                      {
                        count: String(readiness.data.missingSettingNames.length),
                        names: readiness.data.missingSettingNames.join(', '),
                      },
                    )}
                  </Notice>
                ) : null}

                <p className="set__say">
                  {t(
                    'Turn this on and pushes to {branch} stop updating your live app. They go to staging, and your live app only changes when you push a prod tag. You can switch back at any time.',
                    { branch },
                  )}
                </p>
                <div className="set__actions">
                  <Button
                    variant="outline"
                    disabled={busy || !readiness.data?.workflowOk}
                    onClick={() => act('staging_enabled', () => enableStaging(project.id))}
                  >
                    {t('Send pushes to staging')}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </SectionPanel>
  );
}

